import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronDown, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One opaque ID per browser — generated once, then reused for every vote on
// this device. Lets us dedupe spam without ever storing an IP or login state.
// CSPRNG-backed to satisfy Sonar S2245 (predictable visitor IDs would let
// a bad actor pre-compute IDs and stuff votes).
const _randomHex = (bytes = 8) => {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
};
const getOrCreateVisitorId = () => {
  try {
    let id = localStorage.getItem("bc_visitor_id");
    if (!id) {
      id = `v_${_randomHex(8)}${Date.now().toString(36)}`;
      localStorage.setItem("bc_visitor_id", id);
    }
    return id;
  } catch (_) {
    // Private mode → fall back to an in-memory per-session ID.
    return `v_session_${_randomHex(8)}`;
  }
};

const readPriorVotes = () => {
  try {
    return JSON.parse(localStorage.getItem("bc_faq_votes") || "{}");
  } catch (_) {
    return {};
  }
};
const writePriorVote = (qid, helpful) => {
  try {
    const all = readPriorVotes();
    all[qid] = helpful;
    localStorage.setItem("bc_faq_votes", JSON.stringify(all));
  } catch (_) { /* private mode — silently ignore */ }
};

// ---------------------------------------------------------------------------
// Public FAQ page. Mirrors the marketing pages' cream aesthetic. The same
// five questions are mirrored verbatim in the FAQPage JSON-LD block in
// public/index.html — keeping them visually published here gives Google a
// content anchor to attach the rich-result snippet to.
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    id: "what-is-bilateral-stimulation",
    question: "What is bilateral stimulation?",
    answer: (
      <>
        <p>
          Bilateral stimulation is the alternating left-right input — through
          sound, eye movement, or tapping — that sits at the heart of
          therapies like <strong>EMDR (Eye Movement Desensitization and
          Reprocessing)</strong>. The repeating left-right rhythm engages
          both brain hemispheres and helps the nervous system process
          anxiety, anxiety of insomnia, stress, and trauma responses.
        </p>
        <p>
          Bilateral Calm guides you through the same principles in short,
          self-paced sessions you can do anywhere — no clinic visit required.
          It's a self-help tool, not a substitute for therapy, but it pairs
          beautifully with healing work you may be doing in other areas of
          your life.
        </p>
      </>
    ),
  },
  {
    id: "how-is-bilateral-sound-different",
    question: "How is bilateral sound different from regular meditation audio?",
    answer: (
      <>
        <p>
          Bilateral sound deliberately pans between your left and right ears —
          the same alternating rhythm used in EMDR. That left-right pattern
          is the active ingredient: it engages both hemispheres of the brain
          and can help down-regulate anxiety, racing thoughts, and stress
          more quickly than passive listening alone.
        </p>
        <p>
          Regular meditation audio tends to be a constant ambient soundscape
          — relaxing, but not directional. Bilateral Calm's tones, bells,
          pulses, and singing bowls are engineered as stereo bilateral
          stimulation so headphones are recommended for the full effect.
        </p>
      </>
    ),
  },
  {
    id: "insomnia-and-sleep-anxiety",
    question: "Can Bilateral Calm help with insomnia and sleep anxiety?",
    answer: (
      <>
        <p>
          Yes. Bilateral Calm includes dedicated practices targeting the
          <em> fear of sleeplessness</em> that can emerge from insomnia — two
          of the most common reasons people first reach for the app. The
          bilateral sound and breath-cue combination is designed to quiet a
          racing mind so you can calm your central nervous system and drift
          off more easily.
        </p>
        <p>
          A typical pattern of use is to: lie down with headphones, follow
          the guided tasks for 5–15 minutes, and let the bilateral rhythm
          carry your nervous system into a calmer state before sleep.
        </p>
      </>
    ),
  },
  {
    id: "vs-emdr-therapy",
    question: "Is Bilateral Calm the same as EMDR therapy?",
    answer: (
      <>
        <p>
          <strong>No.</strong> Bilateral Calm is a self-help tool
          <em> inspired by</em> EMDR principles — it isn't therapy and
          doesn't replace working with a licensed mental health provider
          for trauma or serious mental health conditions.
        </p>
        <p>
          What it is designed for: giving you daily nervous-system
          regulation for calming and tension relief. It can also help
          address issues bothering you in your day-to-day life. Problems
          leading to impaired function or serious mental health challenges
          should be addressed with a licensed mental health professional.
        </p>
      </>
    ),
  },
  {
    id: "supported-conditions",
    question: "What conditions are the practices designed for?",
    answer: (
      <>
        <p>
          The practices are organised around the everyday emotional
          experiences most people face. Each one pairs bilateral sound with
          structured 9-step guided tasks:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>Tension, worry, and overwhelm</li>
          <li>Anxiety about insomnia</li>
          <li>Nightmares</li>
          <li>Stress and burnout</li>
          <li>Trauma responses (as a self-help complement to therapy)</li>
          <li>Body tension</li>
          <li>Healing past painful stories</li>
          <li>Low self-worth</li>
          <li>Stage fright &amp; performance pressure</li>
          <li>Grief, sadness, and anger</li>
          <li>Boundaries and habit-building</li>
        </ul>
        <p>
          A separate "Daily Practices" audio library offers calming tones
          for ongoing use, such as — deep-calm 432 Hz, gamma 40 Hz, ocean
          waves, and forest tones.
        </p>
      </>
    ),
  },
];

const HelpfulWidget = ({ questionId, initialVote }) => {
  // null = no vote yet, true = thumbs up, false = thumbs down.
  const [vote, setVote] = useState(initialVote);
  const [busy, setBusy] = useState(false);

  const cast = async (helpful) => {
    if (busy || vote === helpful) return;
    setBusy(true);
    // Optimistic — flip the UI immediately and reconcile in the background.
    setVote(helpful);
    writePriorVote(questionId, helpful);
    try {
      await axios.post(`${API}/faq/feedback`, {
        question_id: questionId,
        helpful,
        visitor_id: getOrCreateVisitorId(),
      });
    } catch (_) {
      // Failure is non-fatal — the vote stays cached locally; we just lose
      // server-side tally. No toast: we don't want to nag users.
    } finally {
      setBusy(false);
    }
  };

  const voted = vote !== null && vote !== undefined;

  return (
    <div
      className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3"
      data-testid={`faq-helpful-${questionId}`}
    >
      {voted ? (
        <div className="flex items-center gap-2 text-sm text-[#3FA363]" data-testid={`faq-helpful-thanks-${questionId}`}>
          <Check className="w-4 h-4" />
          <span>Thanks for the feedback.</span>
        </div>
      ) : (
        <span className="text-sm text-[#718096]">Was this helpful?</span>
      )}
      <div className="flex items-center gap-2 ml-auto">
        <button
          onClick={() => cast(true)}
          disabled={busy}
          aria-pressed={vote === true}
          aria-label="Helpful"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            vote === true
              ? "bg-[#E7F4EA] border-[#3FA363] text-[#2D7E47]"
              : "bg-white border-slate-200 text-[#4A5568] hover:border-[#3FA363] hover:text-[#2D7E47]"
          }`}
          data-testid={`faq-helpful-yes-${questionId}`}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
          Yes
        </button>
        <button
          onClick={() => cast(false)}
          disabled={busy}
          aria-pressed={vote === false}
          aria-label="Not helpful"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            vote === false
              ? "bg-[#FEF2F0] border-[#D98880] text-[#A65C58]"
              : "bg-white border-slate-200 text-[#4A5568] hover:border-[#D98880] hover:text-[#A65C58]"
          }`}
          data-testid={`faq-helpful-no-${questionId}`}
        >
          <ThumbsDown className="w-3.5 h-3.5" />
          No
        </button>
      </div>
    </div>
  );
};

const FaqItem = ({ item, open, onToggle, priorVote }) => (
  <div
    className="border border-slate-200 rounded-2xl bg-white overflow-hidden transition-shadow hover:shadow-[0_4px_18px_rgba(45,55,72,0.05)]"
    data-testid={`faq-item-${item.id}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left"
      aria-expanded={open}
      data-testid={`faq-toggle-${item.id}`}
    >
      <h3 className="font-outfit font-semibold text-lg sm:text-xl text-[#1B2349] leading-snug">
        {item.question}
      </h3>
      <ChevronDown
        className={`w-5 h-5 text-[#3090A8] flex-shrink-0 transition-transform duration-300 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    <div
      className={`grid transition-all duration-300 ease-out ${
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">
        <div
          className="px-5 sm:px-6 pb-6 text-[#4A5568] leading-relaxed space-y-3"
          data-testid={`faq-answer-${item.id}`}
        >
          {item.answer}
          <HelpfulWidget questionId={item.id} initialVote={priorVote} />
        </div>
      </div>
    </div>
  </div>
);

const FaqPage = () => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(FAQ_ITEMS[0].id);
  // Read once on mount so each accordion knows whether the visitor has
  // already voted on it from this device. We re-read on storage events so
  // multi-tab voting stays in sync, but the common case is one tab.
  const [priorVotes, setPriorVotes] = useState(() => readPriorVotes());
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bc_faq_votes") setPriorVotes(readPriorVotes());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useSeo({
    title:
      "FAQ — Bilateral Calm | Bilateral Stimulation & EMDR for Anxiety, Insomnia, Stress, Trauma",
    description:
      "Answers to common questions about Bilateral Calm: what bilateral stimulation is, how bilateral sound works, EMDR (Eye Movement Desensitization and Reprocessing) principles, and how the app helps with anxiety, insomnia, stress, and trauma.",
    path: "/faq",
  });

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="faq-page">
      <PublicHeader />

      {/* Hero */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pt-8 pb-10 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-4">
          Frequently Asked Questions
        </p>
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl text-[#1B2349] tracking-tight leading-[1.05]">
          Curious how Bilateral Calm works?
        </h1>
        <p className="mt-5 text-lg text-[#4A5568] leading-relaxed">
          Short answers to the questions we hear most — about bilateral
          stimulation, bilateral sound, EMDR, and what to expect for
          anxiety, insomnia, stress, and trauma.
        </p>
      </section>

      {/* FAQ list */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pb-12">
        <div className="space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((item) => (
            <FaqItem
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
              priorVote={priorVotes[item.id] ?? null}
            />
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pb-20">
        <div
          className="rounded-3xl bg-white border border-slate-100 p-8 sm:p-10 text-center shadow-[0_14px_44px_rgba(0,0,0,0.04)]"
          data-testid="faq-cta-card"
        >
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-3">
            Ready to feel the difference?
          </h2>
          <p className="text-[#4A5568] mb-6 leading-relaxed max-w-xl mx-auto">
            Start your 5-day free trial — no card required. Or unlock the
            full library for as little as $5.83/month.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-all duration-200 active:scale-[0.98]"
              data-testid="faq-cta-pricing-btn"
            >
              See plans
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/sign-in")}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#1B2349]/15 text-[#1B2349] font-medium hover:border-[#1B2349]/40 hover:bg-[#FBF6EE] transition-all duration-200 active:scale-[0.98]"
              data-testid="faq-cta-signin-btn"
            >
              Sign in
            </button>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default FaqPage;
